'use client';

import {
  useCommendaOSEntitySchema,
  useCommendaOSRead,
} from '@commenda/api/commenda-os-dsl';
import { useApiScope } from '@commenda/api/scope';
import { Input, LoadingIndicator } from '@commenda/kakashi';
import type {
  DSLContext,
  ExtendedEnumOption,
} from '@commenda/types/src/commenda-os-dsl';
import { useContext, useEffect, useMemo } from 'react';
import { DSLContextMarker } from '../../dsl-constants';
import { FormContext } from '../../hooks/FormContext';
import { GOVERNMENT_IDENTIFIER_TEST_CANDIDATES } from '../../preview/test-value-registry';
import type { DataSourceConfig } from '@commenda/types';
import { STANDARD_CONTEXT_KEYS } from '../../types';
import { isRuleActive } from '../../utils/validation';
import useGovernmentIdentifiers from '@commenda/api/government-identifiers/useGovernmentIdentifiers';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';
import { saveResolvedGovernmentIdentifier } from './saveResolvedGovernmentIdentifier';

interface GovernmentIdentifierProps {
  fieldId: string;
  label?: string;
  placeholder?: string;
  guidance?: { text?: string };
  description?: string;
  validation?: {
    rules?: {
      required?: { enabled?: boolean } | boolean;
    };
  };
  dataSource?: DataSourceConfig;
  props?: {
    /** What the catalog calls this identifier (e.g., 'GID:US:EIN' for an EIN) */
    identifierTypeId?: string | null;
    /** Operation mode: 'read' for display only, 'read-write' for editable */
    mode?: 'read' | 'read-write';
    disabled?: boolean;
    /**
     * Ask for the number the filing returns rather than naming a key. The entity's jurisdiction
     * decides which key that is, so a form that asks this way needs no edit when a new one is
     * offered. Ignored where identifierTypeId names one outright.
     */
    resolveBy?: { issuedBy: 'incorporation' };
  };
}

/**
 * GovernmentIdentifier Component
 *
 * A specialized component for collecting business identifiers (EIN, state tax numbers, etc.)
 * from Commenda OS. This component:
 *
 * 1. Fetches the government identifier schema to get metadata (displayName, description, regexString)
 * 2. Uses the identifier key to read the current value from the corporation
 * 3. Auto-populates label and description from the schema
 * 4. Validates input using the regex pattern from the schema
 * 5. Supports read-only or read-write modes
 *
 * Configuration:
 * - identifierTypeId: What the catalog calls the identifier (e.g., 'GID:US:EIN')
 * - mode: 'read' for display only, 'read-write' for editable
 * - contextBindings: Used to get corporationId from context or previous field
 */
export const GovernmentIdentifierComponent = ({
  fieldId,
  label: labelOverride,
  placeholder: placeholderOverride,
  guidance,
  description: descriptionOverride,
  validation,
  dataSource,
  props = {},
}: GovernmentIdentifierProps) => {
  const {
    formData,
    setFormData,
    updateField,
    errors,
    validateField,
    isFieldVisible,
    isFieldDisabled,
    dslContext,
    isPreview,
    registerSubmitHandler,
    setFieldNotApplicable,
    registerPreviewFillHandler,
  } = useContext(FormContext);
  const { shareToken } = useApiScope();

  const {
    identifierTypeId: declaredTypeId,
    mode = 'read-write',
    disabled: propsDisabled = false,
    resolveBy,
  } = props;

  const contextBindings = dataSource?.commendaOS?.contextBindings;

  // Special marker for form context binding
  const FORM_CONTEXT_MARKER = DSLContextMarker.FORM_CONTEXT;

  // Valid context keys that the DSL backend accepts
  const VALID_CONTEXT_KEYS: readonly string[] = STANDARD_CONTEXT_KEYS;

  // Build effective context by merging global context with bound field values
  const effectiveContext = useMemo(() => {
    const ctx = { ...dslContext };

    if (contextBindings) {
      for (const binding of contextBindings) {
        // If using form context marker, the value should already be in dslContext
        if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
          continue;
        }

        // Skip invalid context keys (e.g., governmentIdentifierId from old form templates)
        if (!VALID_CONTEXT_KEYS.includes(binding.contextKey)) {
          console.warn(
            `[GovernmentIdentifier] Skipping invalid context key: ${binding.contextKey}`
          );
          continue;
        }

        const fieldValue = formData[binding.sourceFieldId];
        if (fieldValue !== undefined) {
          // Convert string values to numbers for ID fields
          ctx[binding.contextKey] =
            typeof fieldValue === 'string' && !isNaN(Number(fieldValue))
              ? Number(fieldValue)
              : (fieldValue as string | number);
        }
      }
    }

    // Filter out any invalid keys that might have been in dslContext
    const filteredCtx: typeof ctx = {};
    for (const key of Object.keys(ctx)) {
      if (VALID_CONTEXT_KEYS.includes(key)) {
        filteredCtx[key] = ctx[key];
      }
    }

    return filteredCtx;
  }, [dslContext, formData, contextBindings]);

  // Check if we have context bindings but the bound field doesn't have a value yet
  // Only check valid context keys (corporationId, companyId, directorId)
  const hasMissingBoundContext = useMemo(() => {
    if (!contextBindings || contextBindings.length === 0) return false;

    // Filter to only valid context bindings
    const validBindings = contextBindings.filter((binding) =>
      VALID_CONTEXT_KEYS.includes(binding.contextKey)
    );

    if (validBindings.length === 0) return false;

    return validBindings.some((binding) => {
      // If using form context, check if the value exists in dslContext
      if (binding.sourceFieldId === FORM_CONTEXT_MARKER) {
        return (
          dslContext?.[binding.contextKey] === undefined ||
          dslContext?.[binding.contextKey] === null
        );
      }

      const fieldValue = formData[binding.sourceFieldId];
      return (
        fieldValue === undefined || fieldValue === null || fieldValue === ''
      );
    });
  }, [contextBindings, formData, dslContext]);

  // What the entity's jurisdiction says it needs. The same rules refuse a write of an identifier
  // the entity is not eligible for, so asking them is what keeps the form and the write agreeing.
  const corporationId = effectiveContext?.corporationId;
  const shouldResolveType = !declaredTypeId && !!resolveBy;
  const { data: entityIdentifiers, error: entityIdentifiersError } =
    useGovernmentIdentifiers(Number(corporationId), {
      skip: !shouldResolveType || !corporationId || hasMissingBoundContext,
    });

  const identifierTypeId = useMemo(() => {
    if (declaredTypeId) return declaredTypeId;
    if (!resolveBy) return null;

    const applicable = entityIdentifiers?.governmentIdentifiers ?? [];
    const match = applicable.find(
      (identifier) => identifier.isIssuedByIncorporation
    );
    return match?.identifierTypeId ?? null;
  }, [declaredTypeId, resolveBy, entityIdentifiers]);

  // Nothing the entity needs answers this, so there is no question to put. A jurisdiction that
  // issues no such identifier is the ordinary case, not a misconfigured form.
  const hasNoApplicableIdentifier =
    shouldResolveType &&
    !identifierTypeId &&
    (entityIdentifiers !== undefined || entityIdentifiersError !== undefined);

  // Only a lookup that returned counts as an answer, so a failed one keeps the field required. Not
  // cleared on unmount: the final submit validates this step again after it has gone.
  useEffect(() => {
    setFieldNotApplicable?.(
      fieldId,
      hasNoApplicableIdentifier && entityIdentifiers !== undefined
    );
  }, [
    setFieldNotApplicable,
    fieldId,
    hasNoApplicableIdentifier,
    entityIdentifiers,
  ]);

  // Fetch the government identifier schema to get enum options with metadata
  const { entitySchema, isLoading: schemaLoading } = useCommendaOSEntitySchema(
    'governmentIdentifier'
  );

  // Find the identifier metadata from the schema's key filter enum options
  const identifierMeta = useMemo(() => {
    if (!entitySchema?.filters?.identifierTypeId?.enumOptions || !identifierTypeId) {
      return null;
    }

    const options = entitySchema.filters.identifierTypeId
      .enumOptions as ExtendedEnumOption[];
    const meta = options.find((opt) => opt.value === identifierTypeId) || null;
    return meta;
  }, [entitySchema, identifierTypeId]);

  // Path: corporation.governmentIdentifiers[identifierTypeId=GID:US:EIN].value
  const readPath = useMemo(() => {
    if (!identifierTypeId || !effectiveContext?.corporationId) return null;
    return `corporation.governmentIdentifiers[identifierTypeId=${identifierTypeId}].value`;
  }, [identifierTypeId, effectiveContext?.corporationId]);

  // Fetch the current value from Commenda OS
  const { data: currentValue, isLoading: valueLoading } =
    useCommendaOSRead<string>(
      readPath,
      effectiveContext as { companyId: number } | undefined,
      { skip: !readPath || hasMissingBoundContext }
    );

  // Use the fetched value as initial value only when the field has never been set
  // (undefined/null). An empty string means the user explicitly cleared the field.
  const value = useMemo(() => {
    const formValue = formData[fieldId];
    if (formValue !== undefined && formValue !== null) {
      return String(formValue);
    }
    return currentValue ?? '';
  }, [formData, fieldId, currentValue]);

  // Prefill reads only identifiers a form names outright, so a resolved one's number never reaches
  // form state, and a required field showing it still counts as unanswered. An entity picked inside
  // the form can change under a seeded number, which submit would then write onto the new one, so
  // only a fixed entity is seeded.
  const isEntityPickedInForm = !!contextBindings?.some(
    (binding) => binding.sourceFieldId !== FORM_CONTEXT_MARKER
  );
  useEffect(() => {
    if (!shouldResolveType || isEntityPickedInForm || !currentValue) return;
    setFormData((prev) =>
      prev[fieldId] === undefined || prev[fieldId] === null
        ? { ...prev, [fieldId]: currentValue }
        : prev
    );
  }, [
    shouldResolveType,
    isEntityPickedInForm,
    currentValue,
    fieldId,
    setFormData,
  ]);

  // Auto-populate label and description from schema metadata
  const effectiveLabel =
    labelOverride || identifierMeta?.label || 'Business ID';
  const effectiveDescription =
    descriptionOverride || identifierMeta?.description;
  const regexPattern = identifierMeta?.regexString;

  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const isReadOnly = mode === 'read';
  const isDisabled =
    propsDisabled ||
    isFieldDisabled(fieldId) ||
    isReadOnly ||
    hasMissingBoundContext;

  // Validate using regex pattern from schema
  const validateWithRegex = (val: string): string | null => {
    if (!val && !isRequired) return null;
    if (!val && isRequired) return 'This field is required';

    if (regexPattern) {
      try {
        const regex = new RegExp(regexPattern);
        if (!regex.test(val)) {
          return `Invalid format for ${effectiveLabel}`;
        }
      } catch {
        // Invalid regex, skip validation
      }
    }

    return null;
  };

  // Validate value whenever it changes (including on initial load/prefill)
  useEffect(() => {
    // Skip validation if we're still loading or missing context
    if (
      schemaLoading ||
      valueLoading ||
      hasMissingBoundContext ||
      !effectiveContext?.corporationId
    ) {
      return;
    }

    // Only validate if we have a value and a regex pattern
    if (value && regexPattern) {
      const regexError = validateWithRegex(value);
      if (regexError) {
        validateField(fieldId, value, {
          ...validation?.rules,
          pattern: {
            enabled: true,
            value: regexPattern,
            message: `Invalid format for ${effectiveLabel}`,
          },
        });
      } else {
        // Clear any existing error if value is now valid
        validateField(fieldId, value, validation?.rules || {});
      }
    }
  }, [
    value,
    regexPattern,
    schemaLoading,
    valueLoading,
    hasMissingBoundContext,
    effectiveContext?.corporationId,
    fieldId,
    effectiveLabel,
    validation?.rules,
  ]);

  // Preview self-fill: a writable identifier with no OS value gets a sample
  // that must pass this identifier's own format regex — digits-only, dashed,
  // or alphanumeric candidates cover the common EIN/tax-id shapes. If none
  // pass, skip honestly rather than plant a value validation will reject.
  useEffect(() => {
    if (!isPreview || !registerPreviewFillHandler || !fieldId) return undefined;
    registerPreviewFillHandler(fieldId, () => {
      if (value) return { kind: 'already-filled' };
      if (mode === 'read') {
        return { kind: 'skipped', reason: 'read-only identifier' };
      }
      if (schemaLoading || valueLoading) return { kind: 'loading' };
      const sampleValue = regexPattern
        ? GOVERNMENT_IDENTIFIER_TEST_CANDIDATES.find((candidate) => {
            try {
              return new RegExp(regexPattern).test(candidate);
            } catch {
              return false;
            }
          })
        : GOVERNMENT_IDENTIFIER_TEST_CANDIDATES[0];
      if (!sampleValue) {
        return {
          kind: 'skipped',
          reason: 'identifier format needs a manual value',
        };
      }
      updateField(fieldId, sampleValue);
      validateField(fieldId, sampleValue, validation?.rules);
      return { kind: 'filled' };
    });
    return () => registerPreviewFillHandler(fieldId, null);
  }, [
    isPreview,
    registerPreviewFillHandler,
    fieldId,
    value,
    mode,
    schemaLoading,
    valueLoading,
    regexPattern,
    updateField,
    validateField,
    validation?.rules,
  ]);

  // Submit writes only identifiers a form names outright; this one's type is known only here. The
  // handler re-registers on every change, so a field that stops applying stops writing.
  useEffect(() => {
    if (!shouldResolveType || isPreview) return;
    registerSubmitHandler?.(fieldId, async () => {
      if (isReadOnly || !identifierTypeId || !corporationId) return;
      if (hasMissingBoundContext) return;
      await saveResolvedGovernmentIdentifier({
        identifierTypeId,
        value,
        heldValue: currentValue ?? undefined,
        context: effectiveContext as DSLContext,
        shareToken,
      });
    });
  }, [
    shouldResolveType,
    isPreview,
    isReadOnly,
    identifierTypeId,
    corporationId,
    hasMissingBoundContext,
    registerSubmitHandler,
    fieldId,
    value,
    currentValue,
    effectiveContext,
    shareToken,
  ]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    updateField(fieldId, newValue);

    // Validate with both custom regex and standard validation rules
    const regexError = validateWithRegex(newValue);
    if (regexError) {
      // Set custom error - this is a bit hacky but works with the form context
      validateField(fieldId, newValue, {
        ...validation?.rules,
        pattern: regexPattern
          ? {
              enabled: true,
              value: regexPattern,
              message: `Invalid format for ${effectiveLabel}`,
            }
          : undefined,
      });
    } else if (validation?.rules) {
      validateField(fieldId, newValue, validation.rules);
    }
  };

  // Show loading state
  if (schemaLoading || valueLoading) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  if (hasNoApplicableIdentifier) return null;

  // Still asking which identifier this entity needs.
  if (shouldResolveType && !identifierTypeId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        <div className="flex items-center justify-center p-4 border rounded-md bg-bg-secondary">
          <LoadingIndicator size={16} />
          <span className="ml-2 text-sm text-text-tertiary">Loading...</span>
        </div>
      </div>
    );
  }

  // Show configuration needed state
  if (!identifierTypeId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="p-4 border border-dashed rounded-md bg-bg-secondary">
          <p className="text-sm text-text-tertiary text-center">
            Configure identifier key in the property editor
          </p>
        </div>
      </div>
    );
  }

  // Show missing context state
  if (hasMissingBoundContext) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <Input
          type="text"
          value=""
          onChange={() => {}}
          placeholder="Select a corporation above first..."
          disabled
        />
      </div>
    );
  }

  // Show missing corporation context state
  if (!effectiveContext?.corporationId) {
    return (
      <div className="mb-4" data-field-id={fieldId}>
        {(effectiveLabel || effectiveDescription) && (
          <LabelWithGuidanceTooltip
            label={effectiveLabel}
            guidance={guidance?.text}
            isRequired={isRequired}
            description={effectiveDescription}
          />
        )}
        <div className="p-4 border border-border-warning rounded-md bg-bg-warning-subtle">
          <p className="text-sm text-text-warning">
            Corporation context required to load business ID.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(effectiveLabel || effectiveDescription) && (
        <LabelWithGuidanceTooltip
          label={effectiveLabel}
          guidance={guidance?.text}
          isRequired={isRequired}
          description={effectiveDescription}
        />
      )}
      <Input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={
          isReadOnly
            ? 'Read-only'
            : placeholderOverride || `Enter ${effectiveLabel}...`
        }
        error={errors[fieldId]}
        disabled={isDisabled}
      />
    </div>
  );
};
